import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  CareerPath,
  AppIcon,
  AppIcons,
  EarlyCareerHires,
  InfoModal,
  BadgeCircle,
} from '@backrs_ui/common-ui';

import { getMarkdownComponents } from '@backrs_ui/common-ui/src/molecules/Markdown/markdownComponents';
import { APP_ROUTE } from '@backrs_ui/grow-ui/src/appRoutes.constants';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { LubavButton } from '@backrs_ui/grow-ui/src/components/Lubav/LubavButton';
import { CareerPathWithScore } from '@backrs_ui/grow-ui/src/models/models';
import { useExplainCareerPathFitMutation } from '@backrs_ui/grow-ui/src/redux/careerPath/careerPath.lubav.api';
import { useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import { useGetRelevanceQuery } from '@backrs_ui/grow-ui/src/redux/majorsGuide/majorsGuide.api';
import {
  selectIsSharedAccountSessionActive,
  selectSharedAccountSessionToken,
} from '@backrs_ui/grow-ui/src/redux/sharedSession/sharedAccountSession';
import {
  selectSharedBlueprintData,
  selectShareToken,
} from '@backrs_ui/grow-ui/src/redux/sharedSession/sharedSession';
import { getBackrsToken } from '@backrs_ui/grow-ui/src/utils/tokenHelpers';

import { Box, Theme, Typography, SxProps, Chip } from '@mui/material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { useAgentContext as useOverviewAgentContext } from './CareerPathOverview.agentContext';
import { useAgentContext as useFitAgentContext } from './CareerPathOverviewFit.agentContext';
import {
  FIT_SCORE_LEVEL_MAP,
  transformEarlyCareerHiresTotal,
  getGrowthIconAndLabel,
  getCaregiverFlexibilityIconAndLabel,
  GROWTH_MAPPING,
} from '../util';

const STRINGS = appStrings.modules.careerPath.detailPage.description;
const SIDEBAR_STRINGS = appStrings.modules.careerPath.detailPage.descriptionSidebar;

const getAccessToken = (
  isShared: boolean,
  shareToken: string | null | undefined,
  isSharedAccountSessionActive: boolean,
  sharedAccountSessionToken: string | null | undefined,
): string | null | undefined => {
  if (isSharedAccountSessionActive && sharedAccountSessionToken) {
    return sharedAccountSessionToken;
  }

  return isShared ? shareToken : getBackrsToken();
};

type CareerPathOverviewProps = Readonly<{
  careerPath: CareerPath;
  earlyCareerHires: EarlyCareerHires;
  careerPathSuggestions: CareerPathWithScore[] | undefined;
  isShared?: boolean;
}>;

const GrowthModalHeader = () => (
  <Box sx={headerContentContainerSx}>
    <Typography
      color={(theme: Theme) => theme.backrs.colors['Gray/900']}
      variant="text-lg-semibold"
    >
      {STRINGS.growthModal.title}
    </Typography>
    <Typography
      color={(theme: Theme) => theme.backrs.colors['Colors/Text/text-tertiary']}
      variant="text-sm-regular"
    >
      {STRINGS.growthModal.subtitle}
    </Typography>
  </Box>
);

// Derive modal icons from the canonical GROWTH_MAPPING
const GROWTH_LEVEL_ICONS: Record<string, AppIcons> = Object.fromEntries(
  Object.entries(GROWTH_MAPPING).map(([key, { icon }]) => [`${key} Growth`, icon]),
);

const GrowthModalBody = () => {
  const growthLevels = STRINGS.growthModal.levels[0];

  return (
    <Box sx={growthLevelCardsContainerSx}>
      {Object.entries(growthLevels).map(([levelName, description]) => (
        <Box key={levelName} sx={growthLevelCardSx}>
          <Box sx={growthLevelCardHeaderSx}>
            <Box sx={growthLevelIconContainerSx}>
              <AppIcon
                iconName={GROWTH_LEVEL_ICONS[levelName] || 'TrendUp01'}
                diameter="20px"
                color="Colors/Base/White"
              />
            </Box>
            <Typography
              variant="text-lg-semibold"
              color={(theme: Theme) => theme.backrs.colors['Colors/Text/text-secondary']}
            >
              {levelName}
            </Typography>
          </Box>
          <Typography
            variant="text-sm-regular"
            color={(theme: Theme) => theme.backrs.colors['Colors/Text/text-tertiary']}
          >
            {description}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

type InfoModalData = Readonly<{
  title: string;
  description: string;
}>;

const InfoModalContent = ({ title, description }: InfoModalData) => (
  <Box sx={headerContentContainerSx}>
    <Typography
      color={(theme: Theme) => theme.backrs.colors['Gray/900']}
      variant="text-lg-semibold"
    >
      {title}
    </Typography>
    <Typography
      color={(theme: Theme) => theme.backrs.colors['Colors/Base/Black']}
      variant="text-sm-regular"
    >
      {description}
    </Typography>
  </Box>
);

type ModalType = 'growth' | 'earlyCareerHires' | null;

export function CareerPathOverview({
  careerPath,
  earlyCareerHires,
  careerPathSuggestions,
  isShared = false,
}: CareerPathOverviewProps) {
  const navigate = useNavigate();
  const [openModal, setOpenModal] = useState<ModalType>(null);
  const [fitExplanation, setFitExplanation] = useState<string>('');

  const mostRecentResponseSetId = useAppSelector(
    (state) => state.userResponseSet.mostRecentUserResponseSet?.id,
  );
  const shareToken = useAppSelector(selectShareToken);
  const sharedBlueprintData = useAppSelector(selectSharedBlueprintData);
  const isSharedAccountSessionActive = useAppSelector(selectIsSharedAccountSessionActive);
  const sharedAccountSessionToken = useAppSelector(selectSharedAccountSessionToken);

  const careerPathSuggestion = careerPathSuggestions?.find(
    (suggestion) => suggestion.careerPath.id === careerPath.id,
  );

  const responseSetIdToUse =
    isShared && sharedBlueprintData
      ? sharedBlueprintData.userResponseSet.id
      : mostRecentResponseSetId;

  const [explainCareerPathFit, { isLoading: isLubavLoading, error: lubavApiError }] =
    useExplainCareerPathFitMutation();
  const abortControllerRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    const generateExplanation = async () => {
      setFitExplanation('');

      const accessToken = getAccessToken(
        isShared,
        shareToken,
        isSharedAccountSessionActive,
        sharedAccountSessionToken,
      );

      if (!responseSetIdToUse || !careerPathSuggestion?.scoreSection || !accessToken) {
        return;
      }

      try {
        const promise = explainCareerPathFit({
          career_path_id: careerPath.id,
          response_set_id: responseSetIdToUse,
          ranking: careerPathSuggestion.scoreSection,
          accessToken,
          onChunk: (chunk: string) => {
            setFitExplanation(chunk);
          },
        });

        abortControllerRef.current = promise.abort;
        await promise.unwrap();
      } catch (error) {
        console.error(error);
      }
    };

    generateExplanation();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current();
      }
    };
  }, [
    careerPath.id,
    careerPathSuggestions,
    responseSetIdToUse,
    explainCareerPathFit,
    careerPathSuggestion?.scoreSection,
    isShared,
    shareToken,
    isSharedAccountSessionActive,
    sharedAccountSessionToken,
  ]);

  // Fetch best-fit majors for this career path
  const { data: relevanceData } = useGetRelevanceQuery({
    mode: 'paths',
    selections: careerPath.name,
    levels: 'High',
  });

  const bestFitMajors = relevanceData?.items ?? [];

  // Agent context registration
  const careerPathFitLabel =
    FIT_SCORE_LEVEL_MAP[careerPathSuggestion?.scoreSection || 'LOW'] || 'Low Fit';

  useOverviewAgentContext(
    careerPath,
    careerPathFitLabel,
    bestFitMajors.map((m) => m.name),
  );
  useFitAgentContext(fitExplanation);

  const earlyCareerHiresValue = transformEarlyCareerHiresTotal(earlyCareerHires);
  const growthValue = getGrowthIconAndLabel(careerPath.growth);
  const caregiverFlexibilityValue = getCaregiverFlexibilityIconAndLabel(
    careerPath.caregiver_flexibility.caregiver_flexibility_score,
  );

  const getFitExplanationText = () => {
    if (fitExplanation) {
      return fitExplanation;
    }

    if (!responseSetIdToUse) {
      return STRINGS.noBlueprintError;
    }

    return '';
  };

  const handleLubavExplainerClick = () => {
    if (!responseSetIdToUse) {
      navigate(APP_ROUTE.SURVEY);
    }
  };

  const handleMajorClick = (majorId: number) => {
    navigate(`${APP_ROUTE.MAJORS}/${majorId}`);
  };

  return (
    <Box sx={containerSx}>
      {/* Left column — description + fit explainer */}
      <Box sx={descriptionColumnSx}>
        <Typography variant="text-md-regular" sx={descriptionTextSx}>
          {careerPath.description}
        </Typography>
        <Box
          id="career-path-lubav-fit-explainer"
          sx={fitExplainerSx(!responseSetIdToUse)}
          onClick={handleLubavExplainerClick}
        >
          <Box sx={fitExplainerContentSx}>
            <Box sx={fitExplainerTextRowSx}>
              <BadgeCircle loading={isLubavLoading} size="md" />
              <Typography variant="text-sm-regular" sx={aiExplanationSx(!responseSetIdToUse)}>
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={getMarkdownComponents('SMALL', navigate)}
                >
                  {getFitExplanationText()}
                </ReactMarkdown>
              </Typography>
            </Box>
            {lubavApiError && (
              <Typography variant="text-sm-regular" color="error" sx={{ mt: 1 }}>
                {STRINGS.lubavError}
              </Typography>
            )}
            <Box sx={fitExplainerButtonSx}>
              <LubavButton
                id="career-path-fit-discuss-lubav-btn"
                label={STRINGS.discussLabel}
                prompt={STRINGS.discussPrompt(careerPath.name, careerPathFitLabel)}
                allowTools={false}
                useGeneralKnowledge
              />
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Right sidebar — line items */}
      <Box sx={sidebarContainerSx}>
        {/* Growth */}
        <SidebarLineItem
          label={STRINGS.growth}
          icon={growthValue.icon}
          value={growthValue.label}
          infoButton={
            <>
              <Box
                id="career-path-growth-info-button"
                sx={infoIconSx}
                component="span"
                onClick={() => setOpenModal('growth')}
              >
                <AppIcon
                  diameter="12px"
                  iconName="HelpCircle"
                  color="Colors/Foreground/fg-brand-secondary"
                />
              </Box>
              <InfoModal
                open={openModal === 'growth'}
                onClose={() => setOpenModal(null)}
                dialogSx={growthDialogSx}
                icon="AnnotationInfo"
                headerContent={<GrowthModalHeader />}
                bodyContent={<GrowthModalBody />}
                hideCircleImage
              />
            </>
          }
        />

        {/* Early Career Hires */}
        <SidebarLineItem
          label={STRINGS.earlyCareerHires}
          icon={earlyCareerHiresValue.icon}
          value={earlyCareerHiresValue.label}
          infoButton={
            <>
              <Box
                id="career-path-early-career-hires-info-button"
                sx={infoIconSx}
                component="span"
                onClick={() => setOpenModal('earlyCareerHires')}
              >
                <AppIcon
                  diameter="12px"
                  iconName="HelpCircle"
                  color="Colors/Foreground/fg-brand-secondary"
                />
              </Box>
              <InfoModal
                open={openModal === 'earlyCareerHires'}
                onClose={() => setOpenModal(null)}
                dialogSx={modalDialogSx}
                bodyContent={
                  <InfoModalContent
                    title={STRINGS.earlyCareerHires}
                    description={STRINGS.earlyCareerHiresModalDescription}
                  />
                }
              />
            </>
          }
        />

        {/* Caregiver Flexibility */}
        <SidebarLineItem
          label={STRINGS.caregiverFlexibility}
          icon={caregiverFlexibilityValue.icon}
          value={caregiverFlexibilityValue.label}
        />

        {/* Best Fit Majors */}
        {bestFitMajors.length > 0 && (
          <Box sx={sidebarSectionSx}>
            <Typography variant="text-sm-medium" sx={sidebarLabelSx}>
              {SIDEBAR_STRINGS.bestFitMajors}
            </Typography>
            <Box sx={chipsContainerSx}>
              {bestFitMajors.map((major) => (
                <Chip
                  key={major.id}
                  id={`career-path-major-${major.id}-chip`}
                  variant="pill"
                  label={major.name}
                  size="md"
                  sx={majorChipSx}
                  type="leading"
                  onClick={() => handleMajorClick(major.id)}
                  clickable
                />
              ))}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
}

function SidebarLineItem({
  label,
  icon,
  value,
  infoButton,
}: {
  label: string;
  icon: AppIcons;
  value: string;
  infoButton?: React.ReactNode;
}) {
  return (
    <Box sx={sidebarSectionSx}>
      <Box sx={sidebarLabelRowSx}>
        <Typography variant="text-sm-medium" sx={sidebarLabelSx}>
          {label}
        </Typography>
        {infoButton}
      </Box>
      <Box sx={iconValueRowSx}>
        <AppIcon iconName={icon} diameter="20px" color="Colors/Foreground/fg-primary" />
        <Typography
          variant="text-sm-medium"
          color={(theme: Theme) => theme.backrs.colors['Colors/Foreground/fg-primary']}
        >
          {value}
        </Typography>
      </Box>
    </Box>
  );
}

// ── Styles ──

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: backrs.spacing['spacing-4xl'],
  alignItems: 'flex-start',
});

const descriptionColumnSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-2xl'],
  flex: '1 1 min(380px, 100%)',
  minWidth: 0,
});

const descriptionTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});

const fitExplainerSx =
  (isClickable: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    borderRadius: backrs.radius['radius-md'],
    borderTopLeftRadius: 0,
    padding: `${backrs.spacing['spacing-lg']} ${backrs.spacing['spacing-xl']}`,
    display: 'flex',
    flexDirection: 'column',
    border: `1px solid ${backrs.colors['Beige/950']}`,
    backgroundColor: backrs.colors['Colors/Background/bg-secondary'],
    gap: backrs.spacing['spacing-sm'],
    ...(isClickable && {
      cursor: 'pointer',
      transition: 'all 0.3s ease-in-out',
      '&:hover': {
        boxShadow: backrs.shadows['shadow-xl'],
        transform: 'translate(-2px, -2px)',
      },
    }),
  });

const fitExplainerContentSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
};

const fitExplainerTextRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: backrs.spacing['spacing-xs'],
  alignItems: 'baseline',
});

const fitExplainerButtonSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  marginTop: backrs.spacing['spacing-sm'],
});

const aiExplanationSx =
  (containsCTA: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    pl: backrs.spacing['spacing-md'],
    '& p': {
      margin: backrs.spacing['spacing-none'],
      padding: backrs.spacing['spacing-none'],
    },
    '& p:not(:last-child)': {
      marginBottom: backrs.spacing['spacing-md'],
    },
    '& ul, & ol': {
      margin: `${backrs.spacing['spacing-md']} ${backrs.spacing['spacing-none']}`,
      paddingLeft: backrs.spacing['spacing-3xl'],
    },
    '& li': {
      marginBottom: backrs.spacing['spacing-xs'],
    },
    '& em, & i': {
      fontStyle: 'italic',
    },
    ...(containsCTA && {
      '& strong, & b': {
        fontWeight: 700,
        color: backrs.colors['Colors/Text/text-brand-secondary'],
      },
    }),
  });

const sidebarContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-2xl'],
  flex: '1 1 280px',
  maxWidth: '280px',
});

const sidebarSectionSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-md'],
});

const sidebarLabelRowSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'row',
};

const sidebarLabelSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Brand/600'],
});

const iconValueRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: backrs.spacing['spacing-sm'],
});

const infoIconSx: SxProps<Theme> = ({ backrs }) => ({
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  marginLeft: backrs.spacing['spacing-xxs'],
});

const chipsContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: backrs.spacing['spacing-sm'],
});

const majorChipSx: SxProps<Theme> = ({ backrs, typography }) => ({
  backgroundColor: backrs.colors['Brand/50'],
  border: `1px solid ${backrs.colors['Brand/200']}`,
  '& .MuiChip-label': {
    color: backrs.colors['Brand/700'],
    ...typography['text-xs-medium'],
  },
  alignSelf: 'flex-start',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: backrs.colors['Brand/100'],
  },
});

const headerContentContainerSx: SxProps<Theme> = ({ backrs }) => ({
  gap: backrs.spacing['spacing-xs'],
});

const modalDialogSx: SxProps<Theme> = {
  '& .MuiDialog-paper': { maxWidth: '343px' },
};

const growthDialogSx: SxProps<Theme> = {
  '& .MuiDialog-paper': { maxWidth: '500px' },
};

const growthLevelCardsContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-lg'],
});

const growthLevelCardSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xs'],
  padding: backrs.spacing['spacing-lg'],
  border: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
  borderRadius: backrs.radius['radius-xl'],
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
});

const growthLevelCardHeaderSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: backrs.spacing['spacing-lg'],
});

const growthLevelIconContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '40px',
  height: '40px',
  borderRadius: backrs.radius['radius-full'],
  backgroundColor: backrs.colors['Brand/500'],
  flexShrink: 0,
});
